import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { showError, showSuccess } from '../utils/notifications';
import Sidebar from '../components/Sidebar';
import MobileHeader from '../components/MobileHeader';
import LoadingIndicator from '../components/LoadingIndicator';
import * as XLSX from 'xlsx';

function CAReports() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState('');
  const [loading, setLoading] = useState(false);
  const [caData, setCaData] = useState([]);
  const [activeSection, setActiveSection] = useState('ca-reports');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [apiSummary, setApiSummary] = useState(null);

  // Verificar autenticación y permisos
  React.useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        navigate('/login');
        return;
      }
      // Aquí podrías consultar el rol desde Firestore si lo necesitas
      setUser(currentUser);
      setUserRole('Admin'); // O el rol real si lo tienes
    });
    return () => unsubscribe();
  }, [navigate]);

  // Función para consultar la API externa
  const fetchCAApi = async () => {
    if (!startDate || !endDate) {
      showError('Error', 'Selecciona ambas fechas');
      return;
    }
    setLoading(true);
    setCaData([]);
    setApiSummary(null);
    try {
      // Usar el proxy local para evitar CORS
      const url = `/api/ca-reports-proxy?startDate=${startDate}&endDate=${endDate}`;
      const res = await fetch(url);
      const json = await res.json();
      if (json.success) {
        setCaData(json.data || []);
        setApiSummary(json);
        showSuccess('Éxito', json.message || 'Datos obtenidos');
      } else {
        showError('Error', json.message || 'No se pudo obtener el resumen');
      }
    } catch (e) {
      showError('Error', 'No se pudo conectar con la API');
    } finally {
      setLoading(false);
    }
  };

  // Exportar a Excel
  const exportToExcel = () => {
    if (!caData.length) {
      showError('Error', 'No hay datos para exportar');
      return;
    }
    try {
      const worksheet = XLSX.utils.json_to_sheet(
        caData.map(row => ({
          'Número CA': row.numberCa,
          'Nombre': row.nombre,
          'Zona': row.zona,
          'Tickets Motos': row.moto,
          'Tickets Autos': row.auto,
          'Total Tickets': row.total_tickets
        }))
      );
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Resumen CA');
      const date = new Date().toLocaleDateString('es-ES').replace(/\//g, '-');
      XLSX.writeFile(workbook, `Resumen_CA_${date}.xlsx`);
      showSuccess('Éxito', 'Reporte exportado correctamente');
    } catch (error) {
      showError('Error', 'No se pudo exportar el reporte');
    }
  };

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      <MobileHeader 
        title="Reporte de CA" 
        userEmail={user?.email}
        userRole={userRole}
        toggleMenu={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar 
          activeSection={activeSection}
          setActiveSection={setActiveSection}
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
        />
        <main className="flex-1 overflow-auto p-4">
          <div className="mb-4 bg-white rounded-lg shadow p-4">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-4 gap-4">
              <div>
                <h2 className="text-xl font-bold">Resumen de Controladores Autorizados</h2>
                <div className="flex flex-wrap gap-2 mt-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Desde</label>
                    <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="px-3 py-1 border border-gray-300 rounded text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Hasta</label>
                    <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="px-3 py-1 border border-gray-300 rounded text-sm" />
                  </div>
                  <button onClick={fetchCAApi} className="px-4 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors mt-5">Consultar</button>
                  <button onClick={exportToExcel} className="px-4 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 transition-colors mt-5">Exportar Excel</button>
                </div>
              </div>
              {apiSummary && (
                <div className="text-right text-xs text-gray-600">
                  <div>CAPs: <span className="font-bold">{apiSummary.total_caps}</span></div>
                  <div>Desde: <span className="font-bold">{apiSummary.startDate}</span></div>
                  <div>Hasta: <span className="font-bold">{apiSummary.endDate}</span></div>
                </div>
              )}
            </div>
            {loading ? (
              <LoadingIndicator />
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Número CA</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Zona</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tickets Motos</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tickets Autos</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Tickets</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {caData.length === 0 && (
                      <tr><td colSpan={6} className="text-center py-8 text-gray-400">No hay datos para mostrar</td></tr>
                    )}
                    {caData.map((row, idx) => (
                      <tr key={row.numberCa + row.nombre} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{row.numberCa}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.nombre}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.zona}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600">{row.moto}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">{row.auto}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-purple-600">{row.total_tickets}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

export default CAReports; 